export type OrganizationRole = "owner" | "admin" | "member" | "upload_only";

export interface User {
  id: string;
  email: string;
  name: string;
  email_verified?: boolean;
}

export interface Organization {
  id: string;
  name: string;
  role: OrganizationRole;
  created_at: string;
  accounting_entities?: AccountingEntity[];
}

export interface AccountingEntity {
  id: string;
  organization_id: string;
  name: string;
  created_at: string;
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  email: string;
  name: string;
  role: OrganizationRole;
  created_at: string;
}

export interface SessionPayload {
  user: User;
  organizations: Organization[];
  active_organization_id: string | null;
  active_accounting_entity_id: string | null;
  csrf_token: string;
  expires_at: string;
}

export interface SignupInput {
  name: string;
  email: string;
  password: string;
}

export interface PasswordResetConsumeInput {
  token: string;
  new_password: string;
}

export type OAuthProvider = "google" | "apple";

export interface OAuthProviderCapability {
  provider: OAuthProvider;
  enabled: boolean;
}

export interface OAuthProvidersResponse {
  providers: OAuthProviderCapability[];
  password_signup_enabled: boolean;
}

export interface OAuthAuthorizationResponse {
  authorization_url: string;
}

export interface OAuthPendingRegistration {
  provider: OAuthProvider;
  email: string;
  name: string;
  csrf_token: string;
  expires_at: string;
}

export interface OAuthSignupInput {
  name: string;
}

export interface OrganizationInvitation {
  id: string;
  organization_id: string;
  organization_name: string;
  invitee_email: string;
  role: OrganizationRole;
  expires_at: string;
  created_at: string;
}

export interface OrganizationInvitationCreated extends OrganizationInvitation {
  invitation_url: string;
}

export interface SignInCredential {
  id: string;
  kind: "password" | "oidc" | "passkey";
  provider: OAuthProvider | null;
  label: string;
  email: string | null;
  verified_at: string | null;
  last_used_at: string | null;
  removable: boolean;
  removal_block_reason?:
    "last_sign_in_method" | "current_session" | "not_supported" | null;
}

export type ApiAccessScope =
  "documents:read" | "artifacts:read" | "transactions:read" | "connections:read";

export interface ApiAccessKey {
  id: string;
  label: string;
  organization_id: string;
  accounting_entity_id: string;
  scopes: ApiAccessScope[];
  token_hint: string;
  created_at: string;
}

export interface CreatedApiAccessKey extends ApiAccessKey {
  token: string;
}

export interface ArchiveDocument {
  id: string;
  source_connection_id: string | null;
  connection_account_id: string | null;
  title: string;
  created_at: string;
  updated_at: string;
  latest_extraction: DocumentExtractionSummary | null;
}

export interface DocumentFile {
  id: string;
  document_extraction_id: string | null;
  kind: string;
  filename: string;
  mime_type: string;
  byte_size: number;
}

export interface DocumentVersion {
  id: string;
  version: number;
  extraction_status: string;
  extraction_error_code: string | null;
  created_at: string;
  files: DocumentFile[];
  latest_extraction: DocumentExtraction | null;
}

export type DocumentExtractionStatus = "pending" | "running" | "succeeded" | "failed";

export interface DocumentExtractionSummary {
  id: string;
  document_version_id: string;
  sequence: number;
  status: DocumentExtractionStatus;
  detected_media_type: string | null;
  e_invoice_standard: string | null;
  e_invoice_profile: string | null;
  ocr_status: string;
  error_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentInvoiceLine {
  id: string;
  position: number;
  line_id: string | null;
  description: string | null;
  seller_sku: string | null;
  buyer_sku: string | null;
  standard_item_id: string | null;
  quantity: string | null;
  unit_code: string | null;
  unit_price: string | null;
  price_base_quantity: string | null;
  line_net_amount: string | null;
  line_tax_amount: string | null;
  tax_category_code: string | null;
  tax_rate: string | null;
  order_line_reference: string | null;
  additional_fields: Record<string, unknown>;
  created_at: string;
}

export interface DocumentInvoiceTaxSubtotal {
  id: string;
  position: number;
  tax_category_code: string | null;
  tax_rate: string | null;
  taxable_amount: string | null;
  tax_amount: string | null;
  exemption_reason: string | null;
  exemption_reason_code: string | null;
  additional_fields: Record<string, unknown>;
  created_at: string;
}

export interface DocumentInvoicePayment {
  id: string;
  position: number;
  payment_method_code: string | null;
  payment_reference: string | null;
  due_date: string | null;
  iban: string | null;
  bic: string | null;
  account_name: string | null;
  mandate_reference: string | null;
  creditor_identifier: string | null;
  additional_fields: Record<string, unknown>;
  created_at: string;
}

export interface DocumentInvoice {
  id: string;
  source_kind: string;
  document_standard: string | null;
  profile_id: string | null;
  invoice_type_code: string | null;
  invoice_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  service_period_start: string | null;
  service_period_end: string | null;
  seller_name: string | null;
  seller_vat_id: string | null;
  seller_tax_id: string | null;
  buyer_name: string | null;
  buyer_vat_id: string | null;
  buyer_reference: string | null;
  currency_code: string | null;
  total_net_amount: string | null;
  total_tax_amount: string | null;
  total_gross_amount: string | null;
  amount_due: string | null;
  paid_amount: string | null;
  rounding_amount: string | null;
  payment_reference: string | null;
  additional_fields: Record<string, unknown>;
  created_at: string;
  lines: DocumentInvoiceLine[];
  tax_subtotals: DocumentInvoiceTaxSubtotal[];
  payments: DocumentInvoicePayment[];
}

export interface DocumentExtraction {
  id: string;
  document_version_id: string;
  sequence: number;
  status: DocumentExtractionStatus;
  parser_name: string;
  parser_version: string;
  detected_media_type: string | null;
  page_count: number | null;
  has_text_layer: boolean | null;
  born_digital: boolean | null;
  likely_scan: boolean | null;
  encrypted: boolean | null;
  signature_count: number | null;
  e_invoice_standard: string | null;
  e_invoice_profile: string | null;
  ocr_status: string;
  ocr_engine: string | null;
  warnings: string[];
  additional_fields: Record<string, unknown>;
  error_code: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
  files: DocumentFile[];
  invoice: DocumentInvoice | null;
}

export interface TablePreviewCell {
  display: string;
  type: string;
  formula: boolean;
  truncated: boolean;
}

export interface TablePreviewSheet {
  name: string;
  rows: TablePreviewCell[][];
}

export interface TablePreview {
  schema_version: 1;
  format: "csv" | "tsv" | "xlsx";
  warnings: string[];
  sheets: TablePreviewSheet[];
}

export interface SourceArtifact {
  id: string;
  source_connection_id: string | null;
  connection_account_id: string | null;
  source_identity: string;
  source_revision: string | null;
  version: number;
  filename: string;
  mime_type: string;
  sha256: string;
  retrieved_at: string;
}

export interface ManualUploadResponse {
  artifact: SourceArtifact;
  document: ArchiveDocument;
  document_version: DocumentVersion;
  artifact_created: boolean;
  document_version_created: boolean;
}

export type ProductValuationMethod = "manual" | "last" | "average" | "fifo";

export interface ProductValues {
  manual: string | null;
  last: string | null;
  average: string | null;
  fifo: string | null;
  selected: string | null;
  currency_code: string;
  mixed_currencies: boolean;
}

export interface ProductSummary {
  id: string;
  name: string;
  description: string | null;
  valuation_method: ProductValuationMethod;
  identifiers: Array<{ kind: string; value: string }>;
  values: ProductValues;
  observation_count: number;
  updated_at: string;
}

export interface ProductCostObservation {
  id: string;
  observed_on: string;
  unit_cost: string;
  currency_code: string;
  quantity: string | null;
  source_kind: string;
  source_label: string | null;
  source_artifact_id: string | null;
}

export interface ProductDetail extends ProductSummary {
  observations: ProductCostObservation[];
}

export type ReconciliationMatchStatus =
  "proposed" | "confirmed" | "rejected" | "unmatched" | "ambiguous" | "review_required";

export interface ReconciliationSummary {
  statement_count: number;
  entry_count: number;
  proposed_count: number;
  confirmed_count: number;
  rejected_count: number;
  visible_unmatched_count: number;
  visible_ambiguous_count: number;
  visible_review_required_count: number;
  exception_counts_complete: boolean;
  classification_limit: number;
}

export interface FinancialStatement {
  id: string;
  financial_account_id: string;
  source_artifact_id: string;
  provider_statement_id: string;
  period_start: string;
  period_end: string;
  payout_date: string | null;
  currency_code: string;
  total_amount: string;
  entry_total_amount: string;
  opening_balance: string | null;
  closing_balance: string | null;
  entry_count: number;
  importer_name: string;
  importer_version: string;
  imported_at: string;
}

export interface BankStatementImportResponse {
  statements: FinancialStatement[];
  created_count: number;
}

export type BusinessPartyKind = "organization" | "person" | "other";
export interface BusinessParty {
  id: string;
  kind: BusinessPartyKind;
  display_name: string;
  legal_name: string | null;
  vat_id: string | null;
  tax_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type AccountingDocumentDirection = "incoming" | "outgoing";
export type AccountingDocumentType =
  "invoice" | "credit_note" | "receipt" | "expense_claim" | "other";
export type AccountingReviewStatus = "pending" | "reviewed" | "rejected";
export type AccountingTaxTreatment =
  | "standard"
  | "reduced"
  | "zero"
  | "exempt"
  | "reverse_charge"
  | "intra_community"
  | "import"
  | "out_of_scope"
  | "unknown";

export interface AccountingTaxLineInput {
  source_document_invoice_tax_subtotal_id?: string;
  tax_treatment: AccountingTaxTreatment;
  tax_category_code?: string;
  tax_rate?: string;
  taxable_amount: string;
  tax_amount: string;
  deductible_fraction?: string;
  note?: string;
}

export interface AccountingTaxLine {
  id: string;
  source_document_invoice_tax_subtotal_id: string | null;
  position: number;
  tax_treatment: AccountingTaxTreatment;
  tax_category_code: string | null;
  tax_rate: string | null;
  taxable_amount: string;
  tax_amount: string;
  deductible_fraction: string | null;
  note: string | null;
}

export interface AccountingDocumentInput {
  source_document_extraction_id: string;
  source_document_invoice_id?: string;
  business_party_id?: string;
  direction: AccountingDocumentDirection;
  document_type: AccountingDocumentType;
  document_number?: string;
  issue_date?: string;
  due_date?: string;
  service_period_start?: string;
  service_period_end?: string;
  currency_code?: string;
  total_net_amount?: string;
  total_tax_amount?: string;
  total_gross_amount?: string;
  outstanding_amount?: string;
  payment_reference?: string;
  tax_lines: AccountingTaxLineInput[];
}

export interface AccountingDocument {
  id: string;
  document_id: string;
  source_document_version_id: string;
  source_document_extraction_id: string;
  source_document_invoice_id: string | null;
  business_party_id: string | null;
  direction: AccountingDocumentDirection;
  document_type: AccountingDocumentType;
  review_status: AccountingReviewStatus;
  document_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  service_period_start: string | null;
  service_period_end: string | null;
  currency_code: string | null;
  total_net_amount: string | null;
  total_tax_amount: string | null;
  total_gross_amount: string | null;
  outstanding_amount: string | null;
  payment_reference: string | null;
  created_by_user_id: string;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  tax_lines: AccountingTaxLine[];
  created_at: string;
  updated_at: string;
}

export type OpenItemDirection = "payable" | "receivable";
export interface OpenItem {
  id: string;
  accounting_document_id: string;
  business_party_id: string | null;
  direction: OpenItemDirection;
  status: "open" | "partially_settled" | "settled" | "written_off" | "cancelled";
  original_amount: string;
  settled_amount: string;
  currency_code: string;
  due_date: string | null;
  reference: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ReconciliationInvoiceCandidate {
  invoice_id: string;
  document_id: string;
  invoice_number: string | null;
  seller_name: string | null;
  issue_date: string | null;
  amount: string;
  currency_code: string;
  confidence: string;
  explanation: string;
}

export interface OpenItemSettlementAllocation {
  id: string;
  open_item_id: string;
  allocated_amount: string;
  currency_code: string;
  allocation_method: "manual";
  reviewed_by_user_id: string;
  reviewed_at: string;
}

export interface ReconciliationEntry {
  id: string;
  statement_id: string;
  provider_statement_id: string;
  financial_account_id: string;
  financial_account_name: string;
  source_row_number: number;
  source_kind: string;
  entry_kind: string;
  posted_at: string | null;
  amount: string;
  currency_code: string;
  counterparty: string | null;
  description: string | null;
  reference: string | null;
  match_status: ReconciliationMatchStatus;
  candidates: ReconciliationInvoiceCandidate[];
  open_item_allocations: OpenItemSettlementAllocation[];
}

export interface BrowserCapabilities {
  browser_enabled: boolean;
  assistant_enabled: boolean;
}

export interface BrowserSession {
  id: string;
  connection_auth_id: string;
  state: "stopped" | "starting" | "running" | "needs_takeover" | "error";
  started_at: string | null;
  stopped_at: string | null;
  last_activity_at: string | null;
  error_code: string | null;
}

export type ConnectionAuthScheme =
  "browser_profile" | "oauth2_refresh_token" | "api_key_basic" | "file_import";

export interface ConnectorAuthAdapterCatalog {
  key: string;
  scheme: ConnectionAuthScheme;
  runtime_available: boolean;
  runtime_required_secret_kinds: string[];
  secret_kinds: string[];
  capabilities: string[];
  account_kinds: string[];
  artifact_mime_types: string[];
}

export interface ConnectorCatalog {
  key: string;
  label: string;
  capabilities: string[];
  account_kinds: string[];
  artifact_mime_types: string[];
  auth_adapters: ConnectorAuthAdapterCatalog[];
}

export type ConnectionAuthStatus =
  | "setup_required"
  | "ready"
  | "action_required"
  | "expiring"
  | "expired"
  | "error"
  | "disabled";

export interface ConnectionAuthMethod {
  id: string;
  adapter_key: string;
  scheme: ConnectionAuthScheme;
  label: string;
  status: ConnectionAuthStatus;
  subject_hint: string | null;
  expires_at: string | null;
  rotation_due_at: string | null;
  last_verified_at: string | null;
  last_used_at: string | null;
  last_success_at: string | null;
  error_code: string | null;
  capabilities: string[];
  configured_secret_kinds: string[];
  browser: BrowserSession | null;
}

export interface ConnectionSecretPresence {
  secret_kind: string;
  configured: boolean;
  auth_status: ConnectionAuthStatus;
}

export interface ConnectionAccount {
  id: string;
  parent_id: string | null;
  kind: string;
  label: string;
  identifier_hint: string | null;
  status: "active" | "inactive" | "unknown";
  safe_configuration: Record<string, unknown>;
}

export interface SourceConnection {
  id: string;
  connector: string;
  label: string;
  is_enabled: boolean;
  created_at: string;
  browser: BrowserSession | null;
  auth_methods: ConnectionAuthMethod[];
  accounts: ConnectionAccount[];
}

export interface CreateConnectionInput {
  connector: string;
  label: string;
  auth_adapter_key?: string;
}

export interface CreateConnectionAccountInput {
  kind: string;
  external_identity: string;
  label: string;
  parent_id?: string;
  status?: "active" | "inactive" | "unknown";
}

export interface IngestionRun {
  id: string;
  status:
    "pending" | "running" | "waiting_user" | "needs_takeover" | "succeeded" | "failed";
  active_user_action_request_id: string | null;
  source_period: string | null;
  artifact_count: number;
  error_code: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface UserActionRequest {
  id: string;
  ingestion_run_id: string;
  action_kind: string;
  status: string;
  expires_at: string;
  notification_state: string;
  created_at: string;
  completed_at: string | null;
  cancelled_at: string | null;
}

export interface TakeoverTicket {
  websocket_url: string;
  ticket_expires_at: string;
  lease_id: string;
  lease_expires_at: string;
  vnc_password: string;
}

export interface AssistantSession {
  id: string;
  status: "ready" | "running" | "closed" | "error";
  provider: string;
  model: string;
  enabled: boolean;
}

export interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  created_at: string;
}

export interface InvestigatorCapabilities {
  enabled: boolean;
  provider: string | null;
  model: string | null;
  model_spec: string;
  prototype: true;
  drafts_execute: false;
  customer_personal_data_allowed: false;
}

export type InvestigatorArtifactKind = "invoice_documents" | "bank_statements";

export interface InvestigatorSession {
  id: string;
  browser_session_id: string;
  source_connection_id: string;
  goal: string;
  target_origin: string;
  artifact_kind: InvestigatorArtifactKind;
  provider: string;
  model: string;
  prompt_manifest_version: string;
  tool_manifest_version: string;
  status: "ready" | "running" | "error" | "closed";
  last_error_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface InvestigatorMessage {
  id: string;
  sequence: number;
  role: "user" | "assistant";
  content: string;
  action_log: Array<Record<string, unknown>>;
  created_at: string;
}

export interface InvestigatorDraft {
  id: string;
  version: number;
  adapter_version: string;
  connector_key: string;
  recipe: Record<string, unknown>;
  manifest: Record<string, unknown>;
  source: string;
  source_sha256: string;
  status: "draft" | "ready_for_review";
  created_at: string;
  submitted_at: string | null;
}

export interface InvestigatorDetail {
  session: InvestigatorSession;
  messages: InvestigatorMessage[];
  drafts: InvestigatorDraft[];
}

export type AgentPage =
  | "overview"
  | "documents"
  | "document"
  | "transactions"
  | "connections"
  | "connection"
  | "settings"
  | "account";

export type AgentTransactionFilter =
  | "all"
  | "proposed"
  | "confirmed"
  | "rejected"
  | "unmatched"
  | "ambiguous"
  | "review_required";

export type AgentScope =
  | { kind: "overview" }
  | { kind: "documents"; query?: string }
  | {
      kind: "document";
      document_id: string;
      version_id?: string;
      extraction_id?: string;
      invoice_id?: string;
      file_id?: string;
    }
  | { kind: "connections"; prospective_connector?: string }
  | {
      kind: "connection";
      connection_id: string;
      connector: string;
      browser_session_id?: string;
    }
  | {
      kind: "transactions";
      filter: AgentTransactionFilter;
      statement_id?: string;
      entry_id?: string;
    }
  | {
      kind: "settings";
      section: "workspace" | "members" | "accounting";
    }
  | { kind: "account" };

export interface AgentContextRequest {
  schema_version: 1;
  pathname: string;
  scope: AgentScope;
}

export interface AgentResolvedContext {
  schema_version: 1;
  page: AgentPage;
  label: string;
  description: string | null;
  scope: AgentScope;
}

export type AgentNavigationDestination =
  "/" | "/documents" | "/transactions" | "/connections" | "/settings" | "/account";

export type AgentMessageAction =
  | {
      kind: "navigate";
      label: string;
      to: AgentNavigationDestination;
      search?: Record<string, string>;
    }
  | {
      kind: "review_change_set";
      label: string;
      change_set_id: string;
    }
  | {
      kind: "begin_connector_auth";
      label: string;
      connection_id: string;
    };

export interface AgentCapabilities {
  enabled: boolean;
  provider: string | null;
  model: string | null;
  default_model_spec: string | null;
  models: AgentModelOption[];
}

export interface AgentModelOption {
  spec: string;
  provider: string;
  model: string;
  label: string;
}

export interface AgentConversation {
  id: string;
  title: string | null;
  purpose: string;
  status: "active" | "archived";
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface AgentMessage {
  id: string;
  run_id: string | null;
  sequence: number;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  context: AgentResolvedContext | null;
  actions: AgentMessageAction[];
  created_at: string;
}

export interface AgentMessageResponse {
  conversation: AgentConversation;
  messages: AgentMessage[];
}

export type AgentChangeSetStatus =
  | "draft"
  | "ready"
  | "applying"
  | "applied"
  | "rejected"
  | "superseded"
  | "failed"
  | "conflicted"
  | "undone";
export type AgentChangeObjectType =
  | "datev_export_profile"
  | "chart_of_accounts_edition"
  | "ledger_account"
  | "account_mapping_rule";
export type AgentChangeOperation = "create" | "update" | "archive";
export type AgentProposedChangeValue =
  | string
  | number
  | boolean
  | null
  | AgentProposedChangeValue[]
  | { [key: string]: AgentProposedChangeValue };

export interface AgentChangeEvidence {
  kind: string;
  reference: string;
  label?: string;
}

export interface AgentProposedChange {
  id?: string;
  object_type: AgentChangeObjectType;
  object_label?: string;
  operation: AgentChangeOperation;
  object_id?: string;
  expected_revision?: number;
  before: Record<string, AgentProposedChangeValue> | null;
  after: Record<string, AgentProposedChangeValue> | null;
  rationale?: string;
  evidence?: AgentChangeEvidence[];
}

export type AgentProposedChangeInput = Omit<AgentProposedChange, "id" | "before">;

export interface AgentMutationBatch {
  id: string;
  status: "pending" | "failed" | "applied" | "undone";
  undoable: boolean;
  undo_block_reason: string | null;
  applied_at: string;
  undone_at: string | null;
}

export interface AgentChangeSet {
  id: string;
  title: string;
  summary: string | null;
  status: AgentChangeSetStatus;
  status_reason: string | null;
  changes: AgentProposedChange[];
  mutation_batch: AgentMutationBatch | null;
  created_at: string;
  applied_at: string | null;
  undone_at: string | null;
}

export interface CreateAgentChangeSetInput {
  title: string;
  summary?: string;
  conversation_id?: string;
  run_id?: string;
  changes: AgentProposedChangeInput[];
}

export interface DatevExportProfile {
  id: string;
  revision: number;
  name: string;
  chart_of_accounts_edition_id: string;
  consultant_number: string | null;
  client_number: string | null;
  fiscal_year_start_month: number;
  fiscal_year_start_day: number;
  base_currency_code: string;
  accounting_basis: "cash" | "accrual";
  vat_accounting_method: "ist" | "soll";
  authority: "provisional" | "pending_approval" | "approved";
  effective_from: string;
  effective_to: string | null;
  status: "active" | "archived";
}

export interface ChartOfAccountsEdition {
  id: string;
  revision: number;
  code: string;
  display_name: string;
  edition_year: number;
  source_kind: "datev_standard" | "custom";
  account_number_length: number;
  effective_from: string;
  effective_to: string | null;
  status: "active" | "archived";
}

export interface LedgerAccount {
  id: string;
  revision: number;
  chart_of_accounts_edition_id: string;
  account_number: string;
  display_name: string;
  account_type: string;
  normal_balance: "debit" | "credit";
  tax_key: string | null;
  effective_from: string;
  effective_to: string | null;
  status: "active" | "archived";
}

export interface AccountMappingRule {
  id: string;
  revision: number;
  label: string;
  source_connection_id: string | null;
  connection_account_id: string | null;
  marketplace_code: string | null;
  event_type: string | null;
  country_code: string | null;
  tax_treatment: string | null;
  currency_code: string | null;
  ledger_account_id: string;
  counter_ledger_account_id: string | null;
  cost_center_1: string | null;
  cost_center_2: string | null;
  priority: number;
  effective_from: string;
  effective_to: string | null;
  status: "active" | "archived";
}

export interface AccountingConfig {
  datev_export_profile: DatevExportProfile | null;
  chart_of_accounts_edition: ChartOfAccountsEdition | null;
  ledger_accounts: LedgerAccount[];
  account_mapping_rules: AccountMappingRule[];
}

export interface VatRegistration {
  id: string;
  country_code: string;
  vat_id: string;
  registered_name: string;
  address_line_1: string;
  address_line_2: string | null;
  postal_code: string;
  city: string;
  region: string | null;
  valid_from: string;
  valid_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface VatRegistrationInput {
  country_code: string;
  vat_id: string;
  registered_name: string;
  address_line_1: string;
  address_line_2?: string | null;
  postal_code: string;
  city: string;
  region?: string | null;
  valid_from: string;
  valid_to?: string | null;
}

export type BookingBatchStatus = "validated" | "validation_failed";
export type BookingExportFormat = "xlsx" | "excel_csv" | "datev" | "pdf";

export interface BookingBatch {
  id: string;
  profile_revision_id: string;
  period_start: string;
  period_end: string;
  status: BookingBatchStatus;
  profile_authority: "provisional" | "pending_approval" | "approved";
  advisor_source_ready: boolean;
  advisor_source_readiness_reasons: string[];
  advisor_ready: boolean;
  advisor_readiness_reasons: string[];
  source_manifest: Record<string, unknown>[];
  control_totals: Record<string, unknown>;
  validation_report: Record<string, unknown>;
  explained_differences: Record<string, unknown>[];
  row_count: number;
  supersedes_batch_id: string | null;
  generated_at: string;
}

export interface BookingRow {
  id: string;
  sequence: number;
  component: string;
  entry_type: string;
  booking_date: string;
  signed_amount: string;
  currency_code: string;
  debit_account: string;
  credit_account: string;
  tax_key: string | null;
  document_field_1: string | null;
  document_field_2: string | null;
  booking_text: string | null;
  marketplace: string | null;
  source_artifact_id: string | null;
  statement_entry_id: string | null;
  document_id: string | null;
  document_file_id: string | null;
  provenance: Record<string, unknown>;
}

export interface ProvisionalBookingProfile {
  profile_revision_id: string;
  profile_revision: number;
  chart_of_accounts_edition_id: string;
  warning: string;
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

export interface LoginInput {
  email: string;
  password: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  csrfToken?: string;
  rawBody?: BodyInit;
}

function errorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== "object") {
    return fallback;
  }

  const candidate = payload as { detail?: unknown; message?: unknown };
  if (typeof candidate.detail === "string") {
    return candidate.detail;
  }
  if (candidate.detail && typeof candidate.detail === "object") {
    const detail = candidate.detail as { reasons?: unknown };
    if (Array.isArray(detail.reasons)) {
      const reasons = detail.reasons.filter(
        (reason): reason is string => typeof reason === "string",
      );
      if (reasons.length > 0) return reasons.join(" ");
    }
  }
  if (typeof candidate.message === "string") {
    return candidate.message;
  }
  return fallback;
}

async function apiRequest<T>(
  path: string,
  { body, csrfToken, headers, rawBody, ...options }: ApiRequestOptions = {},
): Promise<T> {
  if (body !== undefined && rawBody !== undefined) {
    throw new Error("An API request cannot contain both JSON and raw bodies.");
  }
  const requestHeaders = new Headers(headers);
  requestHeaders.set("Accept", "application/json");
  if (body !== undefined) {
    requestHeaders.set("Content-Type", "application/json");
  }
  if (csrfToken) {
    requestHeaders.set("X-CSRF-Token", csrfToken);
  }

  const response = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers: requestHeaders,
    ...(rawBody === undefined
      ? body === undefined
        ? {}
        : { body: JSON.stringify(body) }
      : { body: rawBody }),
  });

  const contentType = response.headers.get("content-type") ?? "";
  const responseText = contentType.includes("application/json")
    ? await response.text()
    : "";
  const payload: unknown = responseText ? JSON.parse(responseText) : null;

  if (!response.ok) {
    const code =
      payload &&
      typeof payload === "object" &&
      "code" in payload &&
      typeof payload.code === "string"
        ? payload.code
        : undefined;
    throw new ApiError(
      errorMessage(payload, `Request failed with status ${response.status}`),
      response.status,
      code,
    );
  }

  return payload as T;
}

function responseFilename(response: Response, fallback: string): string {
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return fallback;
    }
  }
  return disposition.match(/filename="([^"]+)"/i)?.[1] || fallback;
}

async function apiDownload(
  path: string,
  fallbackFilename: string,
  { body, csrfToken, headers, ...options }: ApiRequestOptions = {},
): Promise<DownloadedFile> {
  const requestHeaders = new Headers(headers);
  requestHeaders.set("Accept", "*/*");
  if (body !== undefined) requestHeaders.set("Content-Type", "application/json");
  if (csrfToken) requestHeaders.set("X-CSRF-Token", csrfToken);
  const response = await fetch(path, {
    ...options,
    credentials: "same-origin",
    headers: requestHeaders,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload: unknown = contentType.includes("application/json")
      ? await response.json()
      : null;
    throw new ApiError(
      errorMessage(payload, `Request failed with status ${response.status}`),
      response.status,
    );
  }
  return {
    blob: await response.blob(),
    filename: responseFilename(response, fallbackFilename),
  };
}

export function saveDownloadedFile({ blob, filename }: DownloadedFile): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.download = filename;
  anchor.href = url;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function apiTextRequest(path: string): Promise<string> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { Accept: "text/plain, application/json;q=0.9, */*;q=0.8" },
  });
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    const payload: unknown = contentType.includes("application/json")
      ? await response.json()
      : null;
    throw new ApiError(
      errorMessage(payload, `Request failed with status ${response.status}`),
      response.status,
    );
  }
  return response.text();
}

const MAX_TABLE_PREVIEW_BYTES = 5 * 1024 * 1024;
const MAX_TABLE_PREVIEW_SHEETS = 20;
const MAX_TABLE_PREVIEW_ROWS = 500;
const MAX_TABLE_PREVIEW_COLUMNS = 50;
const MAX_TABLE_PREVIEW_CELLS = 100_000;
const MAX_TABLE_PREVIEW_CELL_CHARACTERS = 2_000;

async function readBoundedResponseBytes(
  response: Response,
  maxBytes: number,
): Promise<Uint8Array> {
  if (!response.body) {
    const body = new Uint8Array(await response.arrayBuffer());
    if (body.byteLength > maxBytes) {
      throw new Error("The spreadsheet preview exceeds the safe display limit.");
    }
    return body;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let byteLength = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    byteLength += value.byteLength;
    if (byteLength > maxBytes) {
      await reader.cancel();
      throw new Error("The spreadsheet preview exceeds the safe display limit.");
    }
    chunks.push(value);
  }
  const body = new Uint8Array(byteLength);
  let offset = 0;
  for (const chunk of chunks) {
    body.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return body;
}

async function apiBoundedJsonRequest(path: string, maxBytes: number): Promise<unknown> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {
      Accept: "application/vnd.tax2.table-preview+json, application/json;q=0.9",
    },
  });
  const declaredLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredLength) && declaredLength > maxBytes) {
    throw new Error("The spreadsheet preview exceeds the safe display limit.");
  }
  const body = await readBoundedResponseBytes(response, maxBytes);
  if (body.byteLength > maxBytes) {
    throw new Error("The spreadsheet preview exceeds the safe display limit.");
  }

  let payload: unknown = null;
  if (body.byteLength > 0) {
    try {
      const text = new TextDecoder("utf-8", { fatal: true }).decode(body);
      payload = JSON.parse(text) as unknown;
    } catch {
      throw new Error("The spreadsheet preview response was invalid.");
    }
  }
  if (!response.ok) {
    throw new ApiError(
      errorMessage(payload, `Request failed with status ${response.status}`),
      response.status,
    );
  }
  return payload;
}

function previewRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("The spreadsheet preview response was invalid.");
  }
  return value as Record<string, unknown>;
}

function boundedPreviewString(value: unknown, maximum: number): string {
  if (typeof value !== "string" || value.length > maximum) {
    throw new Error("The spreadsheet preview response was invalid.");
  }
  return value;
}

export function parseTablePreview(value: unknown): TablePreview {
  const candidate = previewRecord(value);
  if (
    candidate.schema_version !== 1 ||
    (candidate.format !== "csv" &&
      candidate.format !== "tsv" &&
      candidate.format !== "xlsx") ||
    !Array.isArray(candidate.warnings) ||
    candidate.warnings.length > 100 ||
    !Array.isArray(candidate.sheets) ||
    candidate.sheets.length > MAX_TABLE_PREVIEW_SHEETS
  ) {
    throw new Error("The spreadsheet preview response was invalid.");
  }

  const warnings = candidate.warnings.map((warning) =>
    boundedPreviewString(warning, 100),
  );
  let cellCount = 0;
  const sheets = candidate.sheets.map((sheetValue): TablePreviewSheet => {
    const sheet = previewRecord(sheetValue);
    if (!Array.isArray(sheet.rows) || sheet.rows.length > MAX_TABLE_PREVIEW_ROWS) {
      throw new Error("The spreadsheet preview response was invalid.");
    }
    const rows = sheet.rows.map((rowValue): TablePreviewCell[] => {
      if (!Array.isArray(rowValue) || rowValue.length > MAX_TABLE_PREVIEW_COLUMNS) {
        throw new Error("The spreadsheet preview response was invalid.");
      }
      cellCount += rowValue.length;
      if (cellCount > MAX_TABLE_PREVIEW_CELLS) {
        throw new Error("The spreadsheet preview response was invalid.");
      }
      return rowValue.map((cellValue): TablePreviewCell => {
        const cell = previewRecord(cellValue);
        return {
          display: boundedPreviewString(
            cell.display,
            MAX_TABLE_PREVIEW_CELL_CHARACTERS,
          ),
          type: boundedPreviewString(cell.type, 64),
          formula: cell.formula === true,
          truncated: cell.truncated === true,
        };
      });
    });
    return {
      name: boundedPreviewString(sheet.name, MAX_TABLE_PREVIEW_CELL_CHARACTERS),
      rows,
    };
  });
  return {
    schema_version: 1,
    format: candidate.format,
    warnings,
    sheets,
  };
}

function manualUploadIdentity(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

export function documentFileContentUrl(fileId: string, download = false): string {
  const path = `/api/document-files/${encodeURIComponent(fileId)}/content`;
  return download ? `${path}?download=true` : path;
}

export function validatedAuthorizationUrl(value: string): string {
  let authorizationUrl: URL;
  try {
    authorizationUrl = new URL(value);
  } catch {
    throw new Error("The sign-in provider returned an invalid address.");
  }
  if (
    authorizationUrl.protocol !== "https:" ||
    authorizationUrl.username ||
    authorizationUrl.password
  ) {
    throw new Error("The sign-in provider returned an invalid address.");
  }
  return authorizationUrl.href;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isAccountingEntity(value: unknown): value is AccountingEntity {
  if (!isRecord(value)) {
    return false;
  }
  return ["id", "organization_id", "name", "created_at"].every(
    (key) => typeof value[key] === "string",
  );
}

function isOrganization(value: unknown): value is Organization {
  if (!isRecord(value)) {
    return false;
  }
  const accountingEntities = value.accounting_entities;
  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    ["owner", "admin", "member", "upload_only"].includes(String(value.role)) &&
    typeof value.created_at === "string" &&
    (accountingEntities === undefined ||
      (Array.isArray(accountingEntities) &&
        accountingEntities.every(isAccountingEntity)))
  );
}

export function parsePasswordResetSession(value: unknown): SessionPayload {
  if (!isRecord(value) || !isRecord(value.user)) {
    throw new Error("The password reset response was invalid.");
  }
  const user = value.user;
  const organizations = value.organizations;
  const activeOrganizationId = value.active_organization_id;
  const activeAccountingEntityId = value.active_accounting_entity_id;
  if (
    typeof user.id !== "string" ||
    typeof user.email !== "string" ||
    typeof user.name !== "string" ||
    typeof user.email_verified !== "boolean" ||
    !Array.isArray(organizations) ||
    !organizations.every(isOrganization) ||
    (activeOrganizationId !== null && typeof activeOrganizationId !== "string") ||
    (activeAccountingEntityId !== null &&
      typeof activeAccountingEntityId !== "string") ||
    typeof value.csrf_token !== "string" ||
    value.csrf_token.length === 0 ||
    typeof value.expires_at !== "string" ||
    (activeOrganizationId !== null &&
      !organizations.some((organization) => organization.id === activeOrganizationId))
  ) {
    throw new Error("The password reset response was invalid.");
  }
  return value as unknown as SessionPayload;
}

function parseOAuthProvidersResponse(value: unknown): OAuthProvidersResponse {
  if (!value || typeof value !== "object") {
    throw new Error("The sign-in methods response was invalid.");
  }

  const candidate = value as {
    password_signup_enabled?: unknown;
    providers?: unknown;
  };
  if (
    typeof candidate.password_signup_enabled !== "boolean" ||
    !Array.isArray(candidate.providers)
  ) {
    throw new Error("The sign-in methods response was invalid.");
  }

  const providers = candidate.providers.map((entry): OAuthProviderCapability => {
    if (!entry || typeof entry !== "object") {
      throw new Error("The sign-in methods response was invalid.");
    }
    const provider = (entry as { provider?: unknown }).provider;
    const enabled = (entry as { enabled?: unknown }).enabled;
    if (
      (provider !== "google" && provider !== "apple") ||
      typeof enabled !== "boolean"
    ) {
      throw new Error("The sign-in methods response was invalid.");
    }
    return { provider, enabled };
  });

  if (new Set(providers.map(({ provider }) => provider)).size !== providers.length) {
    throw new Error("The sign-in methods response was invalid.");
  }

  return {
    password_signup_enabled: candidate.password_signup_enabled,
    providers,
  };
}

export const api = {
  getSession: () => apiRequest<SessionPayload>("/api/auth/session"),
  getAuthProviders: async () =>
    parseOAuthProvidersResponse(await apiRequest<unknown>("/api/auth/providers")),
  startOAuth: (provider: OAuthProvider) =>
    apiRequest<OAuthAuthorizationResponse>(
      `/api/auth/oauth/${encodeURIComponent(provider)}/start`,
      { method: "POST" },
    ),
  getPendingOAuthRegistration: () =>
    apiRequest<OAuthPendingRegistration>("/api/auth/oauth/pending"),
  completeOAuthRegistration: (input: OAuthSignupInput, pendingCsrfToken: string) =>
    apiRequest<SessionPayload>("/api/auth/oauth/complete", {
      method: "POST",
      csrfToken: pendingCsrfToken,
      body: input,
    }),
  listSignInCredentials: () => apiRequest<SignInCredential[]>("/api/auth/credentials"),
  startOAuthLink: (provider: OAuthProvider, csrfToken: string) =>
    apiRequest<OAuthAuthorizationResponse>(
      `/api/auth/oauth/${encodeURIComponent(provider)}/link/start`,
      { method: "POST", csrfToken },
    ),
  removeSignInCredential: (credentialId: string, csrfToken: string) =>
    apiRequest<void>(`/api/auth/credentials/${encodeURIComponent(credentialId)}`, {
      method: "DELETE",
      csrfToken,
    }),
  getCurrentApiAccessKey: () =>
    apiRequest<ApiAccessKey | null>("/api/api-keys/current"),
  createApiAccessKey: (
    input: { label: string; scopes: ApiAccessScope[] },
    csrfToken: string,
  ) =>
    apiRequest<CreatedApiAccessKey>("/api/api-keys", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  revokeApiAccessKey: (csrfToken: string) =>
    apiRequest<void>("/api/api-keys/current", {
      method: "DELETE",
      csrfToken,
    }),
  listOrganizationInvitations: (organizationId: string) =>
    apiRequest<OrganizationInvitation[]>(
      `/api/organizations/${encodeURIComponent(organizationId)}/invitations`,
    ),
  createOrganizationInvitation: (
    organizationId: string,
    input: { email: string; role: OrganizationRole },
    csrfToken: string,
  ) =>
    apiRequest<OrganizationInvitationCreated>(
      `/api/organizations/${encodeURIComponent(organizationId)}/invitations`,
      { method: "POST", body: input, csrfToken },
    ),
  claimOrganizationInvitation: (token: string) =>
    apiRequest<OrganizationInvitation>("/api/organization-invitations/claim", {
      method: "POST",
      body: { token },
    }),
  getOrganizationInvitationClaim: () =>
    apiRequest<OrganizationInvitation>("/api/organization-invitations/claim"),
  acceptOrganizationInvitationClaim: (csrfToken: string) =>
    apiRequest<SessionPayload>("/api/organization-invitations/claim/accept", {
      method: "POST",
      csrfToken,
    }),
  revokeOrganizationInvitation: (
    organizationId: string,
    invitationId: string,
    csrfToken: string,
  ) =>
    apiRequest<void>(
      `/api/organizations/${encodeURIComponent(organizationId)}/invitations/${encodeURIComponent(invitationId)}`,
      { method: "DELETE", csrfToken },
    ),
  listMyOrganizationInvitations: () =>
    apiRequest<OrganizationInvitation[]>("/api/organization-invitations"),
  acceptOrganizationInvitation: (invitationId: string, csrfToken: string) =>
    apiRequest<SessionPayload>(
      `/api/organization-invitations/${encodeURIComponent(invitationId)}/accept`,
      { method: "POST", csrfToken },
    ),
  declineOrganizationInvitation: (invitationId: string, csrfToken: string) =>
    apiRequest<void>(
      `/api/organization-invitations/${encodeURIComponent(invitationId)}/decline`,
      { method: "POST", csrfToken },
    ),
  signup: (input: SignupInput) =>
    apiRequest<SessionPayload>("/api/auth/signup", { method: "POST", body: input }),
  login: (input: LoginInput) =>
    apiRequest<SessionPayload>("/api/auth/login", { method: "POST", body: input }),
  consumePasswordReset: async (input: PasswordResetConsumeInput) =>
    parsePasswordResetSession(
      await apiRequest<unknown>("/api/auth/password-reset/consume", {
        method: "POST",
        body: input,
      }),
    ),
  logout: (csrfToken: string) =>
    apiRequest<void>("/api/auth/logout", { method: "POST", csrfToken }),
  switchOrganization: (organizationId: string, csrfToken: string) =>
    apiRequest<SessionPayload>("/api/auth/switch-organization", {
      method: "POST",
      csrfToken,
      body: { organization_id: organizationId },
    }),
  switchAccountingEntity: (accountingEntityId: string, csrfToken: string) =>
    apiRequest<SessionPayload>("/api/auth/switch-accounting-entity", {
      method: "POST",
      csrfToken,
      body: { accounting_entity_id: accountingEntityId },
    }),
  listOrganizations: () => apiRequest<Organization[]>("/api/organizations"),
  createOrganization: (name: string, csrfToken: string) =>
    apiRequest<Organization>("/api/organizations", {
      method: "POST",
      csrfToken,
      body: { name },
    }),
  getOrganization: (organizationId: string) =>
    apiRequest<Organization>(`/api/organizations/${organizationId}`),
  listMembers: (organizationId: string) =>
    apiRequest<OrganizationMember[]>(`/api/organizations/${organizationId}/members`),
  addMember: (
    organizationId: string,
    input: { email: string; role: OrganizationRole },
    csrfToken: string,
  ) =>
    apiRequest<OrganizationMember>(`/api/organizations/${organizationId}/members`, {
      method: "POST",
      csrfToken,
      body: input,
    }),
  updateMember: (
    organizationId: string,
    memberId: string,
    role: OrganizationRole,
    csrfToken: string,
  ) =>
    apiRequest<OrganizationMember>(
      `/api/organizations/${organizationId}/members/${memberId}`,
      { method: "PATCH", csrfToken, body: { role } },
    ),
  removeMember: (organizationId: string, memberId: string, csrfToken: string) =>
    apiRequest<void>(`/api/organizations/${organizationId}/members/${memberId}`, {
      method: "DELETE",
      csrfToken,
    }),
  listAccountingEntities: () =>
    apiRequest<AccountingEntity[]>("/api/accounting-entities"),
  createAccountingEntity: (input: { name: string }, csrfToken: string) =>
    apiRequest<AccountingEntity>("/api/accounting-entities", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listVatRegistrations: () => apiRequest<VatRegistration[]>("/api/vat-registrations"),
  createVatRegistration: (input: VatRegistrationInput, csrfToken: string) =>
    apiRequest<VatRegistration>("/api/vat-registrations", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  updateVatRegistration: (
    registrationId: string,
    input: Partial<Omit<VatRegistrationInput, "country_code">>,
    csrfToken: string,
  ) =>
    apiRequest<VatRegistration>(
      `/api/vat-registrations/${encodeURIComponent(registrationId)}`,
      { method: "PATCH", csrfToken, body: input },
    ),
  listDocuments: (query = "") => {
    const normalizedQuery = query.trim();
    const suffix = normalizedQuery ? `?q=${encodeURIComponent(normalizedQuery)}` : "";
    return apiRequest<ArchiveDocument[]>(`/api/documents${suffix}`);
  },
  downloadDocument: (documentId: string) =>
    apiDownload(`/api/document-exports/${encodeURIComponent(documentId)}`, "document"),
  downloadDocumentsZip: (
    input: {
      document_ids?: string[];
      select_all?: boolean;
      period_start?: string;
      period_end?: string;
      document_types?: Array<
        "invoice" | "settlement" | "seller_fee_invoice" | "vat_report" | "other"
      >;
    },
    csrfToken: string,
  ) =>
    apiDownload("/api/document-exports/zip", "Tax2-documents.zip", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listDocumentVersions: (documentId: string) =>
    apiRequest<DocumentVersion[]>(
      `/api/documents/${encodeURIComponent(documentId)}/versions`,
    ),
  uploadDocument: (
    file: File,
    csrfToken: string,
    source?: { connectionId: string; accountId?: string },
  ) =>
    apiRequest<ManualUploadResponse>("/api/documents/uploads", {
      method: "POST",
      csrfToken,
      headers: {
        "Content-Type": file.type || "application/octet-stream",
        "X-Filename-Encoded": encodeURIComponent(file.name),
        "X-Source-Identity": encodeURIComponent(manualUploadIdentity(file)),
        ...(source
          ? {
              "X-Source-Connection-Id": source.connectionId,
              ...(source.accountId
                ? { "X-Connection-Account-Id": source.accountId }
                : {}),
            }
          : {}),
      },
      rawBody: file,
    }),
  listProducts: () => apiRequest<ProductSummary[]>("/api/products"),
  getProduct: (productId: string) =>
    apiRequest<ProductDetail>(`/api/products/${encodeURIComponent(productId)}`),
  updateProduct: (
    productId: string,
    input: {
      name?: string;
      description?: string | null;
      valuation_method?: ProductValuationMethod;
      manual_unit_cost?: string | null;
      manual_currency_code?: string;
    },
    csrfToken: string,
  ) =>
    apiRequest<ProductDetail>(`/api/products/${encodeURIComponent(productId)}`, {
      method: "PATCH",
      csrfToken,
      body: input,
    }),
  importTaxfbaProducts: (sourceArtifactId: string, csrfToken: string) =>
    apiRequest<{ products_updated: number; observations_created: number }>(
      "/api/products/imports/taxfba",
      {
        method: "POST",
        csrfToken,
        body: { source_artifact_id: sourceArtifactId },
      },
    ),
  importAmazonVatReport: (
    file: File,
    connectionId: string,
    reportPeriod: string,
    csrfToken: string,
  ) =>
    apiRequest<ManualUploadResponse>(
      `/api/connections/${encodeURIComponent(connectionId)}/imports/amazon-vat`,
      {
        method: "POST",
        csrfToken,
        headers: {
          "Content-Type": file.type || "text/csv",
          "X-Filename-Encoded": encodeURIComponent(file.name),
          "X-Report-Period": reportPeriod,
        },
        rawBody: file,
      },
    ),
  importAmazonSettlementReport: (
    file: File,
    connectionId: string,
    marketplaceAccountId: string,
    csrfToken: string,
  ) =>
    apiRequest<ManualUploadResponse>(
      `/api/connections/${encodeURIComponent(connectionId)}/imports/amazon-settlement`,
      {
        method: "POST",
        csrfToken,
        headers: {
          "Content-Type": file.type || "text/tab-separated-values",
          "X-Filename-Encoded": encodeURIComponent(file.name),
          "X-Marketplace-Account-Id": marketplaceAccountId,
        },
        rawBody: file,
      },
    ),
  reprocessDocumentVersion: (
    documentId: string,
    versionId: string,
    csrfToken: string,
  ) =>
    apiRequest<DocumentExtractionSummary>(
      `/api/documents/${encodeURIComponent(documentId)}/versions/${encodeURIComponent(versionId)}/reprocess`,
      { method: "POST", csrfToken },
    ),
  getReconciliationSummary: () =>
    apiRequest<ReconciliationSummary>("/api/reconciliation/summary"),
  listFinancialStatements: () =>
    apiRequest<FinancialStatement[]>("/api/reconciliation/statements"),
  importBankStatement: (artifactId: string, csrfToken: string) =>
    apiRequest<BankStatementImportResponse>(
      "/api/reconciliation/bank-statements/import",
      {
        method: "POST",
        csrfToken,
        body: { artifact_id: artifactId },
      },
    ),
  listBusinessParties: () =>
    apiRequest<BusinessParty[]>("/api/accounting/business-parties"),
  createBusinessParty: (
    input: {
      kind: BusinessPartyKind;
      display_name: string;
      legal_name?: string;
      vat_id?: string;
      tax_id?: string;
    },
    csrfToken: string,
  ) =>
    apiRequest<BusinessParty>("/api/accounting/business-parties", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listAccountingDocuments: (reviewStatus?: AccountingReviewStatus) => {
    const suffix = reviewStatus
      ? `?review_status=${encodeURIComponent(reviewStatus)}`
      : "";
    return apiRequest<AccountingDocument[]>(`/api/accounting/documents${suffix}`);
  },
  createAccountingDocument: (input: AccountingDocumentInput, csrfToken: string) =>
    apiRequest<AccountingDocument>("/api/accounting/documents", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  reviewAccountingDocument: (
    accountingDocumentId: string,
    decision: "reviewed" | "rejected",
    csrfToken: string,
  ) =>
    apiRequest<AccountingDocument>(
      `/api/accounting/documents/${encodeURIComponent(accountingDocumentId)}/reviews`,
      { method: "POST", csrfToken, body: { decision } },
    ),
  listOpenItems: (status?: OpenItem["status"]) => {
    const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiRequest<OpenItem[]>(`/api/accounting/open-items${suffix}`);
  },
  createOpenItem: (
    input: {
      accounting_document_id: string;
      business_party_id?: string;
      direction: OpenItemDirection;
      original_amount: string;
      due_date?: string;
      reference?: string;
    },
    csrfToken: string,
  ) =>
    apiRequest<OpenItem>("/api/accounting/open-items", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listReconciliationEntries: (
    status?: ReconciliationMatchStatus,
    statementId?: string,
  ) => {
    const query = new URLSearchParams();
    if (status) query.set("status", status);
    if (statementId) query.set("statement_id", statementId);
    const suffix = query.size ? `?${query.toString()}` : "";
    return apiRequest<ReconciliationEntry[]>(`/api/reconciliation/entries${suffix}`);
  },
  confirmOpenItemSettlement: (
    statementEntryId: string,
    input: { open_item_id: string; allocated_amount: string },
    csrfToken: string,
  ) =>
    apiRequest<OpenItemSettlementAllocation>(
      `/api/reconciliation/entries/${encodeURIComponent(statementEntryId)}/open-item-allocations`,
      { method: "POST", csrfToken, body: input },
    ),
  readDocumentFileText: (fileId: string) =>
    apiTextRequest(documentFileContentUrl(fileId)),
  readDocumentTablePreview: async (fileId: string) =>
    parseTablePreview(
      await apiBoundedJsonRequest(
        documentFileContentUrl(fileId),
        MAX_TABLE_PREVIEW_BYTES,
      ),
    ),
  getConnectionCapabilities: () =>
    apiRequest<BrowserCapabilities>("/api/connections/capabilities"),
  listConnectorCatalog: () =>
    apiRequest<ConnectorCatalog[]>("/api/connections/catalog"),
  listConnections: () => apiRequest<SourceConnection[]>("/api/connections"),
  createConnection: (input: CreateConnectionInput, csrfToken: string) =>
    apiRequest<SourceConnection>("/api/connections", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listConnectionAuthMethods: (connectionId: string) =>
    apiRequest<ConnectionAuthMethod[]>(
      `/api/connections/${encodeURIComponent(connectionId)}/auth-methods`,
    ),
  addConnectionAuthMethod: (
    connectionId: string,
    authAdapterKey: string,
    csrfToken: string,
  ) =>
    apiRequest<ConnectionAuthMethod>(
      `/api/connections/${encodeURIComponent(connectionId)}/auth-methods`,
      {
        method: "POST",
        csrfToken,
        body: { auth_adapter_key: authAdapterKey },
      },
    ),
  setConnectionAuthSecret: (
    connectionId: string,
    authMethodId: string,
    secretKind: string,
    value: string,
    csrfToken: string,
  ) =>
    apiRequest<ConnectionSecretPresence>(
      `/api/connections/${encodeURIComponent(connectionId)}/auth-methods/${encodeURIComponent(authMethodId)}/secrets/${encodeURIComponent(secretKind)}`,
      {
        method: "PUT",
        csrfToken,
        body: { value },
      },
    ),
  removeConnectionAuthSecret: (
    connectionId: string,
    authMethodId: string,
    secretKind: string,
    csrfToken: string,
  ) =>
    apiRequest<ConnectionSecretPresence>(
      `/api/connections/${encodeURIComponent(connectionId)}/auth-methods/${encodeURIComponent(authMethodId)}/secrets/${encodeURIComponent(secretKind)}`,
      { method: "DELETE", csrfToken },
    ),
  listConnectionAccounts: (connectionId: string) =>
    apiRequest<ConnectionAccount[]>(
      `/api/connections/${encodeURIComponent(connectionId)}/accounts`,
    ),
  createConnectionAccount: (
    connectionId: string,
    input: CreateConnectionAccountInput,
    csrfToken: string,
  ) =>
    apiRequest<ConnectionAccount>(
      `/api/connections/${encodeURIComponent(connectionId)}/accounts`,
      { method: "POST", csrfToken, body: input },
    ),
  getBrowserStatus: (connectionId: string) =>
    apiRequest<BrowserSession>(
      `/api/connections/${encodeURIComponent(connectionId)}/browser`,
    ),
  startBrowser: (connectionId: string, csrfToken: string) =>
    apiRequest<BrowserSession>(
      `/api/connections/${encodeURIComponent(connectionId)}/browser/start`,
      { method: "POST", csrfToken },
    ),
  stopBrowser: (connectionId: string, csrfToken: string) =>
    apiRequest<BrowserSession>(
      `/api/connections/${encodeURIComponent(connectionId)}/browser/stop`,
      { method: "POST", csrfToken },
    ),
  createTakeoverTicket: (browserSessionId: string, csrfToken: string) =>
    apiRequest<TakeoverTicket>(
      `/api/browser-sessions/${encodeURIComponent(browserSessionId)}/takeover-ticket`,
      { method: "POST", csrfToken },
    ),
  listConnectionRuns: (connectionId: string) =>
    apiRequest<IngestionRun[]>(
      `/api/connections/${encodeURIComponent(connectionId)}/runs`,
    ),
  createConnectionRun: (
    connectionId: string,
    period: { since: string; until: string },
    csrfToken: string,
  ) =>
    apiRequest<IngestionRun>(
      `/api/connections/${encodeURIComponent(connectionId)}/runs`,
      { method: "POST", csrfToken, body: period },
    ),
  completeUserActionRequest: (requestId: string, csrfToken: string) =>
    apiRequest<UserActionRequest>(
      `/api/user-action-requests/${encodeURIComponent(requestId)}/complete`,
      { method: "POST", csrfToken },
    ),
  createAssistantSession: (browserSessionId: string, csrfToken: string) =>
    apiRequest<AssistantSession>(
      `/api/browser-sessions/${encodeURIComponent(browserSessionId)}/assistant-sessions`,
      { method: "POST", csrfToken },
    ),
  listAssistantMessages: (assistantSessionId: string) =>
    apiRequest<AssistantMessage[]>(
      `/api/assistant-sessions/${encodeURIComponent(assistantSessionId)}/messages`,
    ),
  sendAssistantMessage: (assistantSessionId: string, text: string, csrfToken: string) =>
    apiRequest<AssistantMessage[]>(
      `/api/assistant-sessions/${encodeURIComponent(assistantSessionId)}/messages`,
      { method: "POST", csrfToken, body: { text } },
    ),
  getInvestigatorCapabilities: () =>
    apiRequest<InvestigatorCapabilities>("/api/agent-investigator/capabilities"),
  listInvestigatorSessions: () =>
    apiRequest<InvestigatorSession[]>("/api/agent-investigator/sessions"),
  getInvestigatorSession: (investigatorSessionId: string) =>
    apiRequest<InvestigatorDetail>(
      `/api/agent-investigator/sessions/${encodeURIComponent(investigatorSessionId)}`,
    ),
  createInvestigatorSession: (
    input: {
      browser_session_id: string;
      goal: string;
      target_origin: string;
      artifact_kind: InvestigatorArtifactKind;
      data_policy_acknowledged: true;
    },
    csrfToken: string,
  ) =>
    apiRequest<InvestigatorDetail>("/api/agent-investigator/sessions", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  sendInvestigatorMessage: (
    investigatorSessionId: string,
    text: string,
    csrfToken: string,
  ) =>
    apiRequest<InvestigatorDetail>(
      `/api/agent-investigator/sessions/${encodeURIComponent(investigatorSessionId)}/messages`,
      { method: "POST", csrfToken, body: { text } },
    ),
  submitInvestigatorDraft: (
    investigatorSessionId: string,
    draftId: string,
    csrfToken: string,
  ) =>
    apiRequest<InvestigatorDraft>(
      `/api/agent-investigator/sessions/${encodeURIComponent(investigatorSessionId)}/drafts/${encodeURIComponent(draftId)}/submit-review`,
      { method: "POST", csrfToken },
    ),
  getAgentCapabilities: () => apiRequest<AgentCapabilities>("/api/agent/capabilities"),
  listAgentConversations: () =>
    apiRequest<AgentConversation[]>("/api/agent/conversations"),
  createAgentConversation: (
    input: { title?: string; purpose?: string },
    csrfToken: string,
  ) =>
    apiRequest<AgentConversation>("/api/agent/conversations", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listAgentConversationMessages: (conversationId: string) =>
    apiRequest<AgentMessage[]>(
      `/api/agent/conversations/${encodeURIComponent(conversationId)}/messages`,
    ),
  sendAgentConversationMessage: (
    conversationId: string,
    text: string,
    context: AgentContextRequest,
    modelSpec: string,
    csrfToken: string,
  ) =>
    apiRequest<AgentMessageResponse>(
      `/api/agent/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        csrfToken,
        body: { text, context, model_spec: modelSpec },
      },
    ),
  getAccountingConfig: () => apiRequest<AccountingConfig>("/api/accounting-config"),
  createProvisionalBookingProfile: (effectiveFrom: string, csrfToken: string) =>
    apiRequest<ProvisionalBookingProfile>("/api/booking-exports/provisional-profile", {
      method: "POST",
      csrfToken,
      body: { effective_from: effectiveFrom },
    }),
  createBookingBatch: (
    input: {
      period_start: string;
      period_end: string;
      profile_revision_id: string;
      supersedes_batch_id?: string;
      excluded_invoice_markers?: string[];
      package_scope?: "store24_full" | "amazon_diagnostic";
    },
    csrfToken: string,
  ) =>
    apiRequest<BookingBatch>("/api/booking-exports/batches", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  listBookingBatches: () => apiRequest<BookingBatch[]>("/api/booking-exports/batches"),
  listBookingRows: (
    batchId: string,
    filters: { component?: string; marketplace?: string; q?: string } = {},
  ) => {
    const query = new URLSearchParams();
    if (filters.component) query.set("component", filters.component);
    if (filters.marketplace) query.set("marketplace", filters.marketplace);
    if (filters.q) query.set("q", filters.q);
    query.set("limit", "5000");
    return apiRequest<BookingRow[]>(
      `/api/booking-exports/batches/${encodeURIComponent(batchId)}/rows?${query}`,
    );
  },
  downloadBookingRows: (
    batchId: string,
    input: {
      format: BookingExportFormat;
      row_ids?: string[];
      component?: string;
      marketplace?: string;
    },
    csrfToken: string,
  ) =>
    apiDownload(
      `/api/booking-exports/batches/${encodeURIComponent(batchId)}/export`,
      "Tax2-bookings",
      { method: "POST", csrfToken, body: input },
    ),
  downloadAdvisorPackage: (
    batchId: string,
    input: {
      reviewed_note?: string;
      mode?: "full" | "belegimport" | "csv" | "pdf_zip";
      purpose?: "production" | "shadow_review";
    },
    csrfToken: string,
  ) =>
    apiDownload(
      `/api/advisor-exports/batches/${encodeURIComponent(batchId)}`,
      "Tax2-advisor-package.zip",
      { method: "POST", csrfToken, body: input },
    ),
  listAgentChangeSets: (status?: AgentChangeSetStatus) => {
    const suffix = status ? `?status=${encodeURIComponent(status)}` : "";
    return apiRequest<AgentChangeSet[]>(`/api/agent/change-sets${suffix}`);
  },
  getAgentChangeSet: (changeSetId: string) =>
    apiRequest<AgentChangeSet>(
      `/api/agent/change-sets/${encodeURIComponent(changeSetId)}`,
    ),
  createAgentChangeSet: (input: CreateAgentChangeSetInput, csrfToken: string) =>
    apiRequest<AgentChangeSet>("/api/agent/change-sets", {
      method: "POST",
      csrfToken,
      body: input,
    }),
  applyAgentChangeSet: (changeSetId: string, csrfToken: string) =>
    apiRequest<AgentChangeSet>(
      `/api/agent/change-sets/${encodeURIComponent(changeSetId)}/apply`,
      { method: "POST", csrfToken },
    ),
  undoAgentChangeSet: (changeSetId: string, csrfToken: string) =>
    apiRequest<AgentChangeSet>(
      `/api/agent/change-sets/${encodeURIComponent(changeSetId)}/undo`,
      { method: "POST", csrfToken },
    ),
};
